import { Box, Container, Typography } from '@mui/material';
import { Helmet } from 'react-helmet';
import SectionHeader from '../components/atomic/SectionHeader';
import Footer from '../components/Footer';
import Header from '../components/Header';

/**
 * Case Study: Operationalizing Clinical Intelligence with AWS Serverless Architecture
 *
 * Detail page for the MTOMICS/healthcare case study about building a serverless
 * lab integration module on AWS. This page does NOT include a sidebar form
 * per client request.
 *
 * STRUCTURE:
 * - Dark header with page title
 * - Full-width content layout (no form sidebar)
 * - The Client section
 * - The Modernization Opportunity section
 * - The Result section
 * - Customer Quote
 * - Solution Overview section with Architecture Highlights
 * - About RSM Collective section
 *
 * @returns {import('react').ReactElement} MTOMICS Clinical Intelligence case study page
 */
function CaseStudyMtomicsClinical() {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline:
      'Operationalizing Clinical Intelligence with AWS Serverless Architecture',
    description:
      'An emerging healthcare company delivered end-to-end lab integration in eight weeks using a fully serverless AWS architecture to power precision metabolic health insights.',
    author: {
      '@type': 'Organization',
      name: 'RSM Collective',
    },
    publisher: {
      '@type': 'Organization',
      name: 'RSM Collective',
      logo: {
        '@type': 'ImageObject',
        url: 'https://www.rsmc.ai/images/r-logo.png',
      },
    },
    image: 'https://www.rsmc.ai/images/hcls-clinical-hero.jpg',
    datePublished: '2026-02-13',
    dateModified: '2026-02-13',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': 'https://www.rsmc.ai/case-studies/hcls-clinical-intelligence',
    },
  };

  /** @type {import('@mui/system').SxProps} */
  const sectionSx = {
    mb: { xs: 6, md: 10 },
    borderLeft: {
      xs: '4px solid var(--color-primary)',
      md: '6px solid var(--color-primary)',
    },
    pl: { xs: 3, md: 4 },
  };

  /** @type {import('@mui/system').SxProps} */
  const headingSx = {
    fontWeight: 800,
    mb: 3,
    color: 'var(--color-heading)',
    fontSize: { xs: '1.75rem', md: '2rem' },
    letterSpacing: '-0.02em',
  };

  /** @type {import('@mui/system').SxProps} */
  const paragraphSx = {
    fontSize: '1.0625rem',
    lineHeight: 1.75,
    color: 'var(--color-text)',
    fontWeight: 400,
    mb: 3,
  };

  /** @type {import('@mui/system').SxProps} */
  const separatorSx = {
    textAlign: 'center',
    my: 6,
  };

  return (
    <>
      <Helmet>
        <title>
          Operationalizing Clinical Intelligence with AWS Serverless
          Architecture | RSM Collective
        </title>
        <meta
          name="description"
          content="An emerging healthcare company delivered end-to-end lab integration in eight weeks using a fully serverless AWS architecture to power precision metabolic health insights."
        />
        <meta
          property="og:title"
          content="Operationalizing Clinical Intelligence with AWS Serverless Architecture"
        />
        <meta
          property="og:description"
          content="An emerging healthcare company delivered end-to-end lab integration in eight weeks using a fully serverless AWS architecture to power precision metabolic health insights."
        />
        <meta property="og:type" content="article" />
        <meta
          property="og:url"
          content="https://www.rsmc.ai/case-studies/hcls-clinical-intelligence"
        />
        <meta
          property="og:image"
          content="https://www.rsmc.ai/images/hcls-clinical-hero.jpg"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Operationalizing Clinical Intelligence with AWS Serverless Architecture"
        />
        <meta
          name="twitter:description"
          content="End-to-end lab integration delivered in eight weeks with AWS serverless architecture."
        />
        <meta
          name="keywords"
          content="AWS serverless, healthcare, lab integration, HIPAA, Lambda, Step Functions, clinical intelligence, metabolic health, case study, RSM Collective"
        />
        <link
          rel="canonical"
          href="https://www.rsmc.ai/case-studies/hcls-clinical-intelligence"
        />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <Header
        scrollToCollective={() => {}}
        scrollToServices={() => {}}
        scrollToContact={() => {}}
      />

      {/* White gap above header */}
      <Box
        sx={{
          backgroundColor: 'var(--color-background)',
          pt: { xs: 8, md: 10 },
        }}
      >
        <SectionHeader
          title={
            <>
              Operationalizing Clinical Intelligence with
              <br />
              AWS Serverless Architecture
            </>
          }
          subtitle=""
          dark={true}
        />
      </Box>

      {/* Main Content Area - Full Width (no form sidebar) */}
      <Box
        sx={{
          backgroundColor: 'var(--color-background)',
          pt: { xs: 3, md: 4 },
          pb: { xs: 4, md: 6 },
        }}
      >
        <Container maxWidth="md" sx={{ px: { xs: 2, md: 3 } }}>
          {/* Hero Image */}
          <Box
            component="img"
            src="/images/hcls-clinical-hero.jpg"
            alt="Healthcare technology visualization with green-tinted digital interface representing clinical intelligence and data-driven metabolic health insights"
            sx={{
              width: '100%',
              height: 'auto',
              maxHeight: { xs: '250px', md: '350px' },
              objectFit: 'cover',
              borderRadius: '8px',
              mb: 6,
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            }}
          />

          <Box component="article">
            {/* The Client Section */}
            <Box sx={sectionSx}>
              <Typography variant="h3" sx={headingSx}>
                The Client
              </Typography>
              <Typography variant="body1" sx={paragraphSx}>
                An emerging healthcare and life sciences company is building a
                next-generation metabolic intelligence platform designed to
                decode metabolic complexity and translate biomarker data into
                personalized, actionable health insights.
              </Typography>
              <Typography variant="body1" sx={paragraphSx}>
                By unifying clinical, lifestyle, genomic, and real-world data,
                the platform moves beyond generic lab reference ranges to
                deliver intelligence that informs individualized therapies and
                lifestyle interventions. This approach enables deeper insight,
                more precise personalization, and a differentiated experience
                across both practitioners and patient use cases.
              </Typography>
            </Box>

            {/* Visual separator */}
            <Box sx={separatorSx}>
              <Box
                component="img"
                src="/images/r-logo.png"
                alt="Section separator"
                sx={{ width: '40px', height: 'auto', opacity: 0.7 }}
              />
            </Box>

            {/* The Modernization Opportunity Section */}
            <Box sx={sectionSx}>
              <Typography variant="h3" sx={headingSx}>
                The Modernization Opportunity
              </Typography>
              <Typography variant="body1" sx={paragraphSx}>
                As the company scaled its platform across both B2B and B2C
                markets, laboratory integration emerged as a strategic
                capability required to support the next phase of growth.
              </Typography>
              <Typography variant="body1" sx={paragraphSx}>
                Following an AWS architecture and platform assessment with RSM
                Collective, the teams aligned on building a secure, serverless
                lab integration layer to connect the platform directly with
                certified diagnostic laboratory partners. This capability
                enables clinical-grade biomarker data to flow into the company's
                proprietary AI-driven metabolic scoring engine, supporting
                automation, trust, and scalability.
              </Typography>
              <Typography variant="body1" sx={paragraphSx}>
                The lab integration module became a critical enabler of the
                company's B2B strategy, providing healthcare and enterprise
                partners with a trusted, automated pathway for lab ordering and
                data ingestion. At the same time, it established the technical
                and clinical foundation needed to accelerate B2C
                adoption--ensuring personalized metabolic intelligence could be
                delivered consistently and at scale.
              </Typography>
              <Typography variant="body1" sx={paragraphSx}>
                The shared objective was to deliver a production-ready,
                HIPAA-eligible integration layer supporting patient
                registration, test ordering, and requisition retrieval--built
                using infrastructure-as-code, automated deployments, and a
                serverless architecture designed to grow alongside the business.
              </Typography>
              <Typography variant="body1" sx={paragraphSx}>
                RSM Collective and the client structured the engagement as a
                fixed-bid, eight-week delivery across four agile sprints,
                embedding security, compliance, and operational resilience as
                core design principles from day one.
              </Typography>
            </Box>

            {/* Visual separator */}
            <Box sx={separatorSx}>
              <Box
                component="img"
                src="/images/r-logo.png"
                alt="Section separator"
                sx={{ width: '40px', height: 'auto', opacity: 0.7 }}
              />
            </Box>

            {/* The Result Section */}
            <Box sx={sectionSx}>
              <Typography variant="h3" sx={headingSx}>
                The Result
              </Typography>
              <Box
                component="ul"
                sx={{
                  pl: 0,
                  listStyle: 'none',
                  '& li': {
                    fontSize: '1.0625rem',
                    lineHeight: 1.75,
                    color: 'var(--color-text)',
                    mb: 2,
                    pl: 4,
                    position: 'relative',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      top: '0.6em',
                      width: '8px',
                      height: '8px',
                      backgroundColor: 'var(--color-primary)',
                      borderRadius: '50%',
                    },
                    '& strong': {
                      color: 'var(--color-heading)',
                      fontWeight: 600,
                    },
                  },
                }}
              >
                <li>
                  <strong>
                    End-to-end lab integration delivered in eight weeks
                  </strong>
                  , enabling patient registration, test ordering, and
                  requisition delivery through a fully serverless AWS
                  architecture.
                </li>
                <li>
                  <strong>Precision intelligence unlocked at scale.</strong> Raw
                  biomarker data now flows directly into the platform's
                  proprietary AI-driven metabolic scoring engine, enabling
                  highly personalized health insights.
                </li>
                <li>
                  <strong>
                    B2B capabilities strengthened to drive B2C growth.
                  </strong>{' '}
                  The integration provides enterprise-grade automation,
                  security, and trust--forming a strong foundation for expanding
                  consumer-facing offerings.
                </li>
                <li>
                  <strong>HIPAA-eligible by design.</strong> Encryption, network
                  isolation, audit logging, and access controls are codified in
                  infrastructure-as-code, ensuring compliance is embedded,
                  repeatable, and auditable.
                </li>
                <li>
                  <strong>Operational efficiency aligned with growth.</strong>{' '}
                  Automatic scaling and pay-per-use economics eliminate
                  infrastructure overhead while supporting increasing demand.
                </li>
                <li>
                  <strong>Built for Growth.</strong> The architecture is
                  designed to expand into multi-test workflows, B2C and B2B
                  platform integration, and production lab connectivity in a
                  subsequent phase without rebuilding what exists.
                </li>
              </Box>
            </Box>

            {/* Customer Quote */}
            <Box
              sx={{
                backgroundColor:
                  'color-mix(in srgb, var(--color-primary) 8%, transparent)',
                borderRadius: '12px',
                p: { xs: 3, md: 4 },
                mb: { xs: 6, md: 10 },
                borderLeft: '4px solid var(--color-primary)',
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontSize: '1.0625rem',
                  lineHeight: 1.75,
                  color: 'var(--color-text)',
                  fontStyle: 'italic',
                  mb: 2,
                }}
              >
                &ldquo;RSM Collective became true partners in our mission.
                Beyond assessment and delivery, they helped us secure funding,
                sharpen our differentiation, and define the right architectural
                path forward. In eight weeks, they delivered a secure,
                serverless foundation that positions us to bring precision
                health directly to consumers. We couldn't have reached this
                milestone without them.&rdquo;
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'var(--color-primary)',
                  fontWeight: 600,
                  fontSize: '0.9375rem',
                }}
              >
                &mdash; CEO, Metabolic Healthcare Company
              </Typography>
            </Box>

            {/* Visual separator */}
            <Box sx={separatorSx}>
              <Box
                component="img"
                src="/images/r-logo.png"
                alt="Section separator"
                sx={{ width: '40px', height: 'auto', opacity: 0.7 }}
              />
            </Box>

            {/* Solution Overview Section */}
            <Box sx={sectionSx}>
              <Typography variant="h3" sx={headingSx}>
                Solution Overview
              </Typography>
              <Typography variant="body1" sx={paragraphSx}>
                RSM Collective designed and delivered a fully serverless lab
                integration module on AWS, built to support patient
                registration, test ordering, requisition retrieval, and
                event-driven data ingestion.
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  color: 'var(--color-heading)',
                  fontSize: { xs: '1.25rem', md: '1.5rem' },
                }}
              >
                Architecture Highlights
              </Typography>
              <Box
                component="ul"
                sx={{
                  pl: 0,
                  listStyle: 'none',
                  '& li': {
                    fontSize: '1.0625rem',
                    lineHeight: 1.75,
                    color: 'var(--color-text)',
                    mb: 2,
                    pl: 4,
                    position: 'relative',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      top: '0.6em',
                      width: '8px',
                      height: '8px',
                      backgroundColor: 'var(--color-primary)',
                      borderRadius: '50%',
                    },
                    '& strong': {
                      color: 'var(--color-heading)',
                      fontWeight: 600,
                    },
                  },
                }}
              >
                <li>
                  <strong>AWS Lambda</strong> for modular, event-driven compute
                  with automatic scaling
                </li>
                <li>
                  <strong>AWS Step Functions</strong> to orchestrate end-to-end
                  lab order workflows
                </li>
                <li>
                  <strong>Amazon EventBridge</strong> for asynchronous events
                  and webhook ingestion
                </li>
                <li>
                  <strong>Amazon API Gateway</strong> for secure API exposure
                </li>
                <li>
                  <strong>Amazon RDS</strong> and <strong>Amazon S3</strong> for
                  encrypted data storage
                </li>
                <li>
                  <strong>Amazon CloudFront</strong> and{' '}
                  <strong>AWS WAF</strong> to secure frontend access
                </li>
                <li>
                  <strong>AWS Secrets Manager</strong> for credential management
                </li>
                <li>
                  <strong>IAM, KMS, VPC</strong> for least-privilege access,
                  encryption, and network isolation
                </li>
              </Box>
              <Typography variant="body1" sx={paragraphSx}>
                All infrastructure and application components were codified
                using Terraform and deployed through an automated CI/CD
                pipeline, ensuring repeatability, auditability, and operational
                ownership.
              </Typography>
              <Typography variant="body1" sx={paragraphSx}>
                Security and compliance were embedded from day one, with
                encryption at rest and in transit, private networking, audit
                logging, and automated security scanning integrated into the
                delivery process.
              </Typography>
            </Box>

            {/* Visual separator */}
            <Box sx={separatorSx}>
              <Box
                component="img"
                src="/images/r-logo.png"
                alt="Section separator"
                sx={{ width: '40px', height: 'auto', opacity: 0.7 }}
              />
            </Box>

            {/* About RSM Collective Section */}
            <Box sx={sectionSx}>
              <Typography variant="h3" sx={headingSx}>
                About RSM Collective
              </Typography>
              <Typography variant="body1" sx={paragraphSx}>
                RSM Collective is a San Diego-based AWS Consulting Partner
                helping startups, non-profits, and regulated industries
                modernize through AWS services and partner ecosystems. We
                specialize in business process transformation using serverless
                and AI/ML architectures, leading every engagement with people
                and process before technology.
              </Typography>
            </Box>

            {/* Final visual separator */}
            <Box sx={{ ...separatorSx, mt: 6, mb: 2 }}>
              <Box
                component="img"
                src="/images/r-logo.png"
                alt="RSM Collective"
                sx={{ width: '50px', height: 'auto', opacity: 0.7 }}
              />
            </Box>
          </Box>
        </Container>
      </Box>

      <Footer />
    </>
  );
}

export default CaseStudyMtomicsClinical;
